import useHttpProvider from "@/common/hooks/use-http";
import { useEffect, useMemo, useState } from "react";
import { ShippingQuote, ShippingSettings } from "../models/shipping";
import * as ShippingService from "../services/shipping";

export function quoteFromSettings(
	settings: ShippingSettings | null,
	subtotal: number
): ShippingQuote | null {
	if (!settings) return null;
	const threshold = Number(settings.freeShippingThreshold || 0);
	const base = Number(settings.baseFee || 0);

	if (subtotal >= threshold) {
		return {
			amount: 0,
			currency: settings.currency,
			free: true,
			freeThreshold: threshold,
			label: "Free shipping",
			message: `Free shipping (orders over $${threshold.toFixed(2)} qualify)`,
		};
	}

	const remaining = Math.max(0, threshold - subtotal);
	return {
		amount: base,
		currency: settings.currency,
		free: false,
		freeThreshold: threshold,
		label: "Standard shipping",
		message: remaining > 0
			? `Spend $${remaining.toFixed(2)} more for free shipping`
			: null,
	};
}

export default function useShipping(subtotal: number) {
	const { http } = useHttpProvider();
	const [settings, setSettings] = useState<ShippingSettings | null>(null);
	const [isLoading, setIsLoading] = useState(true);

	useEffect(() => {
		let cancelled = false;
		(async () => {
			try {
				const data = await ShippingService.getShippingSettings(http);
				if (!cancelled) setSettings(data);
			} catch {
				if (!cancelled) setSettings(null);
			} finally {
				if (!cancelled) setIsLoading(false);
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [http]);

	const quote = useMemo(
		() => quoteFromSettings(settings, subtotal),
		[settings, subtotal]
	);

	return { settings, quote, isLoading };
}
